import { useState } from 'react';
import SEOHead from '../components/SEOHead';
import seoData from '../seo/contact.json';

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '', date: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const encode = (data) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const handleSubmit = (e) => {
    e.preventDefault();
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': 'contact', ...form }),
    })
      .then(() => {
        setSubmitted(true);
        setError(false);
        setForm({ name: '', email: '', phone: '', service: '', date: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      })
      .catch(() => {
        setError(true);
        setTimeout(() => setError(false), 5000);
      });
  };

  return (
    <>
      <SEOHead seo={seoData} />

      <section className="page-header">
        <div className="container">
          <div className="section-label">Get In Touch</div>
          <h1>Contact <em>Us</em></h1>
          <div className="gold-line" />
          <p>
            Ready to book? Call us directly for the fastest service or fill out
            the form below.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div>
              <div className="section-label" style={{ marginBottom: 24 }}>Direct Lines</div>
              <div className="contact-info-cards">
                <a href="tel:8189154566" style={{ textDecoration: 'none' }}>
                  <div className="contact-info-card">
                    <div className="icon-wrap"><PhoneIcon /></div>
                    <div>
                      <h4>Primary Line</h4>
                      <p style={{ fontSize: '1.1rem', color: 'var(--gold)', fontWeight: 500 }}>(818) 915-4566</p>
                      <p style={{ fontSize: '0.78rem', marginTop: 4 }}>Tap to call — Available 24/7</p>
                    </div>
                  </div>
                </a>

                <a href="tel:8183559929" style={{ textDecoration: 'none' }}>
                  <div className="contact-info-card">
                    <div className="icon-wrap"><PhoneIcon /></div>
                    <div>
                      <h4>Secondary Line</h4>
                      <p style={{ fontSize: '1.1rem', color: 'var(--gold)', fontWeight: 500 }}>(818) 355-9929</p>
                      <p style={{ fontSize: '0.78rem', marginTop: 4 }}>Tap to call — Available 24/7</p>
                    </div>
                  </div>
                </a>

                <div className="contact-info-card">
                  <div className="icon-wrap"><LocationIcon /></div>
                  <div>
                    <h4>Service Area</h4>
                    <p>Greater Los Angeles, CA</p>
                    <p style={{ fontSize: '0.78rem', marginTop: 4 }}>LAX, Burbank, Orange County & beyond</p>
                  </div>
                </div>

                <div className="contact-info-card">
                  <div className="icon-wrap"><ClockIcon /></div>
                  <div>
                    <h4>Hours</h4>
                    <p>Available 24 hours a day</p>
                    <p style={{ fontSize: '0.78rem', marginTop: 4 }}>7 days a week, 365 days a year</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <h3>Send Us a Message</h3>

              {submitted && (
                <div className="form-success" style={{ marginBottom: 20 }}>
                  Thank you! We'll get back to you shortly.
                </div>
              )}

              {error && (
                <div className="form-success" style={{ marginBottom: 20, borderColor: '#c44', color: '#c44' }}>
                  Something went wrong. Please call us directly or try again.
                </div>
              )}

              <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit}>
                <input type="hidden" name="form-name" value="contact" />
                <p hidden><label>Don't fill this out: <input name="bot-field" /></label></p>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input type="text" id="name" name="name" value={form.name} onChange={handleChange} placeholder="John Smith" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(310) 555-0000" required />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input type="email" id="email" name="email" value={form.email} onChange={handleChange} placeholder="john@example.com" required />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="service">Service Type</label>
                    <select id="service" name="service" value={form.service} onChange={handleChange} required>
                      <option value="">Select a service</option>
                      <option value="airport">Airport Transfer</option>
                      <option value="corporate">Corporate Travel</option>
                      <option value="wedding">Wedding / Event</option>
                      <option value="nightlife">Nightlife</option>
                      <option value="wine-tour">Wine Tour / Sightseeing</option>
                      <option value="hourly">Hourly Charter</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="date">Preferred Date</label>
                    <input type="date" id="date" name="date" value={form.date} onChange={handleChange} />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message / Details</label>
                  <textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your trip — pickup location, destination, number of passengers, special requests..." rows={5} />
                </div>

                <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                  Send Request
                </button>

                <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 16 }}>
                  For fastest booking, call us directly at{' '}
                  <a href="tel:8189154566" style={{ color: 'var(--gold)' }}>(818) 915-4566</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
